import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Artifact, Video } from "./contracts";

export function VideoEvidence({
  video,
  onArtifact,
  onReference,
}: {
  video: Video;
  onArtifact: (artifact: Artifact) => void;
  onReference?: (assetId: string) => void;
}) {
  return (
    <article
      className="space-y-3 rounded-md border p-3"
      aria-label={video.asset_id}
    >
      <div className="flex flex-wrap gap-2">
        <strong>{video.asset_id}</strong>
        <Badge variant="outline">{video.status}</Badge>
      </div>
      <p className="text-sm">
        These sampled frames do not establish a room assignment for the full
        clip.
      </p>
      <p className="text-sm">
        {video.samples.length} samples from {video.decoded_frames} decoded
        frames.
      </p>
      {video.samples.map((sample) => (
        <div className="space-y-2 border-t pt-2" key={sample.source_frame_us}>
          <strong>
            Frame at {(sample.source_frame_us / 1000000).toFixed(2)}s
          </strong>
          {sample.observation?.thumbnail ? (
            <img
              src={sample.observation.thumbnail.url}
              alt={`Video evidence at ${(sample.source_frame_us / 1000000).toFixed(2)} seconds`}
              className="max-h-64 max-w-full rounded object-contain"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : null}
          {sample.observation ? (
            <ul className="list-disc pl-5 text-sm">
              {sample.observation.evidence.map((item) => (
                <li key={item.evidence_id}>
                  {item.feature} ({item.state}): {item.description}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">
              Preview budget reached. Inspect this video to load its samples.
            </p>
          )}
          <p className="break-all text-xs">
            Observation SHA-256: {sample.artifact.sha256}
          </p>
        </div>
      ))}
      {video.diagnostics.length ? (
        <p className="text-sm">{video.diagnostics.join(", ")}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onArtifact(video.artifact)}
        >
          Inspect video evidence JSON
        </Button>
        {onReference ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReference(video.asset_id)}
          >
            Inspect this video
          </Button>
        ) : null}
      </div>
    </article>
  );
}
